import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AliquotaIbsService } from '@shared/services/apis/motor-fiscal/aliquota-ibs.service';
import { UnidadeFederativaService } from '@shared/services/apis/usuario/unidade-federativa.service';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { forkJoin } from 'rxjs';
import { AliquotaIbsEditDialogComponent, AliquotaIbsEditDialogData, AliquotaIbsEditDialogResult } from '../aliquota-ibs-edit-dialog/aliquota-ibs-edit-dialog.component';

interface LinhaUf {
    idUnidadeFederativa: number;
    uf: string;
    nome: string;
    pAliquota: number;
    dataVigenciaInicio: string | null;
    dataVigenciaFim: string | null;
    personalizada: boolean;
}

import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { LoadingBoxDefaultListComponent } from '@shared/components/loading-box-default-list/loading-box-default-list.component';

/** Listagem/edição das alíquotas IBS por UF (efetivas = base ou override da empresa). Salvamento direto. */
@Component({
    selector: 'app-aliquota-ibs-uf-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatTableModule,
        MatProgressSpinnerModule,
        EmptyStateComponent,
        LoadingBoxDefaultListComponent,
    ],
    templateUrl: './aliquota-ibs-uf-dialog.component.html',
    styleUrl: './aliquota-ibs-uf-dialog.component.css',
})
export class AliquotaIbsUfDialogComponent implements OnInit {
    public isLoading = false;
    public pesquisa = '';
    public displayedColumns = ['uf', 'aliquota', 'vigencia', 'actions'];

    private todas: LinhaUf[] = [];
    public linhas: LinhaUf[] = [];

    constructor(
        private dialogRef: MatDialogRef<AliquotaIbsUfDialogComponent>,
        private dialog: MatDialog,
        private aliquotaService: AliquotaIbsService,
        private ufService: UnidadeFederativaService,
        private alertService: AlertService,
    ) {}

    ngOnInit(): void {
        this.carregar();
    }

    private carregar(): void {
        this.isLoading = true;
        forkJoin({
            ufs: this.ufService.getUnidadesFederativas(),
            aliquotas: this.aliquotaService.getIbsUf(),
        }).subscribe({
            next: ({ ufs, aliquotas }) => {
                const porId = new Map(ufs.map(u => [u.idUnidadeFederativa, u]));
                this.todas = (aliquotas?.itens ?? []).map(a => {
                    const uf = porId.get(a.id);
                    return {
                        idUnidadeFederativa: a.id,
                        uf: uf?.id ?? String(a.id),
                        nome: uf?.descricao ?? '',
                        pAliquota: a.pAliquota,
                        dataVigenciaInicio: a.dataVigenciaInicio,
                        dataVigenciaFim: a.dataVigenciaFim,
                        personalizada: a.personalizada,
                    };
                }).sort((x, y) => x.uf.localeCompare(y.uf));
                this.filtrar();
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                this.alertService.show('Erro', 'Não foi possível carregar as alíquotas por UF.', AlertType.error);
            },
        });
    }

    public filtrar(): void {
        const p = this.pesquisa.trim().toLowerCase();
        this.linhas = !p ? [...this.todas]
            : this.todas.filter(l => l.uf.toLowerCase().includes(p) || l.nome.toLowerCase().includes(p));
    }

    public editar(linha: LinhaUf): void {
        const data: AliquotaIbsEditDialogData = {
            titulo: 'Alíquota IBS UF',
            descricao: `${linha.nome} - ${linha.uf}`,
            pAliquota: linha.pAliquota,
        };
        const ref = this.dialog.open(AliquotaIbsEditDialogComponent, { width: '420px', maxWidth: '95vw', data });
        ref.afterClosed().subscribe((result: AliquotaIbsEditDialogResult | undefined) => {
            if (!result) return;
            this.aliquotaService.salvarIbsUf(linha.idUnidadeFederativa, result.pAliquota).subscribe({
                next: r => {
                    if (r?.isError) {
                        this.alertService.show('Erro', r.errorDescription || 'Não foi possível salvar a alíquota.', AlertType.error);
                        return;
                    }
                    linha.pAliquota = result.pAliquota;
                    linha.personalizada = true;
                    this.alertService.show('Sucesso', 'Alíquota salva com sucesso.', AlertType.success);
                },
                error: () => this.alertService.show('Erro', 'Não foi possível salvar a alíquota.', AlertType.error),
            });
        });
    }

    public fechar(): void {
        this.dialogRef.close();
    }
}
